(() => {
  const root = document.querySelector('[data-fizo-homepage]');
  if (!root) return;

  const authenticated = root.dataset.authenticated === 'true';
  const designerRanking = root.querySelector('[data-fizo-home-designer-ranking]');
  if (designerRanking) {
    const rows = [...designerRanking.querySelectorAll('[data-fizo-home-designer-row]')].map((node) => ({
      node,
      rank: node.querySelector('[data-fizo-home-designer-rank]'),
      count: Number(node.dataset.count) || 0,
      name: node.dataset.name || '',
    }));
    rows
      .sort((left, right) => right.count - left.count || left.name.localeCompare(right.name))
      .forEach((entry, index) => {
        entry.node.hidden = index >= 3;
        if (entry.rank) entry.rank.textContent = String(index + 1);
        designerRanking.append(entry.node);
      });
  }
  const hero = root.querySelector('[data-home-hero]');
  const desktopHeroQuery = window.matchMedia('(min-width: 990px)');

  const fitSticky = root.querySelector('[data-home-fit-sticky]');
  if (hero && fitSticky) {
    let fitStickyFrame = null;
    const setFitStickyVisibility = (visible) => {
      fitSticky.classList.toggle('is-visible', visible);
      fitSticky.setAttribute('aria-hidden', String(!visible));
      fitSticky.tabIndex = visible ? 0 : -1;
    };
    const syncFitStickyVisibility = () => {
      fitStickyFrame = null;
      setFitStickyVisibility(hero.getBoundingClientRect().bottom <= 0);
    };
    const scheduleFitStickyVisibility = () => {
      if (fitStickyFrame !== null) return;
      fitStickyFrame = window.requestAnimationFrame(syncFitStickyVisibility);
    };
    window.addEventListener('scroll', scheduleFitStickyVisibility, {passive: true});
    window.addEventListener('resize', scheduleFitStickyVisibility, {passive: true});
    window.visualViewport?.addEventListener('resize', scheduleFitStickyVisibility, {passive: true});
    syncFitStickyVisibility();
  }

  const fullscreenScenes = [...root.querySelectorAll('[data-home-fullscreen-scene]')];
  const syncFullscreenSceneScale = () => {
    if (!desktopHeroQuery.matches) {
      fullscreenScenes.forEach((scene) => {
        scene.style.removeProperty('--fizo-home-fullscreen-fit-scale');
        scene.style.removeProperty('--fizo-home-fullscreen-height');
        scene.style.removeProperty('--fizo-home-fullscreen-canvas-width');
        scene.style.removeProperty('--fizo-home-fullscreen-canvas-height');
        scene.style.removeProperty('--fizo-home-fullscreen-scroll-distance');
      });
      return;
    }

    const rootFontSize = Number.parseFloat(getComputedStyle(document.documentElement).fontSize) || 10;
    const designHeight = 108 * rootFontSize;
    const viewportHeight = Math.min(
      document.documentElement.clientHeight,
      window.visualViewport?.height || Number.POSITIVE_INFINITY
    );
    const fitScale = Math.min(1, viewportHeight / designHeight);
    const canvasWidth = fitScale < 1 ? 100 / fitScale : 100;
    const canvasHeight = fitScale < 1 ? designHeight : viewportHeight;

    fullscreenScenes.forEach((scene) => {
      const scrollDistance = Number.parseFloat(scene.dataset.homeFullscreenScroll) || 0;
      scene.style.setProperty('--fizo-home-fullscreen-fit-scale', fitScale.toFixed(4));
      scene.style.setProperty('--fizo-home-fullscreen-height', `${viewportHeight.toFixed(2)}px`);
      scene.style.setProperty('--fizo-home-fullscreen-canvas-width', `${canvasWidth.toFixed(4)}%`);
      scene.style.setProperty('--fizo-home-fullscreen-canvas-height', `${canvasHeight.toFixed(2)}px`);
      scene.style.setProperty(
        '--fizo-home-fullscreen-scroll-distance',
        `${(scrollDistance * rootFontSize * fitScale).toFixed(2)}px`
      );
    });
  };
  window.addEventListener('resize', syncFullscreenSceneScale, {passive: true});
  window.visualViewport?.addEventListener('resize', syncFullscreenSceneScale, {passive: true});
  if (desktopHeroQuery.addEventListener) desktopHeroQuery.addEventListener('change', syncFullscreenSceneScale);
  else desktopHeroQuery.addListener(syncFullscreenSceneScale);
  syncFullscreenSceneScale();

  const recommendationEndpoint = root.dataset.recommendationEndpoint;
  const recommendationConfigEndpoint = root.dataset.recommendationConfigEndpoint;
  const recommendationResultsUrl = root.dataset.recommendationResultsUrl || '/collections/all?view=recommendations';
  const recommendationContext = {
    locale: root.dataset.locale || 'en-US',
    currency: root.dataset.currency || 'USD',
  };
  const dialog = root.querySelector('[data-recommendation-dialog]');
  const form = root.querySelector('[data-recommendation-form]');
  const status = root.querySelector('[data-recommendation-status]');
  const productGrid = root.querySelector('[data-product-grid]');
  const storageKey = 'fizo.home.recommendation.answers';
  const configStorageKey = 'fizo.home.recommendation.config.v2';
  const configCacheLifetime = 5 * 60 * 1000;
  const resultStorageKey = 'fizo.home.recommendation.result';
  const anonymousKey = 'fizo.home.anonymous-id';
  const anonymousLifetime = 24 * 60 * 60 * 1000;
  const recommendationSteps = [...root.querySelectorAll('[data-recommendation-step]')];
  const recommendationScroller = root.querySelector('[data-recommendation-steps]');
  const stepCount = root.querySelector('[data-recommendation-step-count]');
  const stepNumber = root.querySelector('[data-recommendation-step-number]');
  const answerChip = root.querySelector('[data-recommendation-answer-chip]');
  const progress = root.querySelector('.fizo-recommendation-progress');
  const progressSteps = [...(progress?.querySelectorAll('[data-recommendation-progress-step]') || [])];
  const backButton = root.querySelector('[data-recommendation-back]');
  const nextButton = root.querySelector('[data-recommendation-next]');
  const submitButton = root.querySelector('.fizo-recommendation-submit');
  let recommendationStep = 1;
  let recommendationAnswers = {};
  let dialogScrollState = null;
  let pendingRecommendationAdvance;
  let lastRecommendationInput;
  let lastRecommendationClickAt = 0;
  let recommendationScrollFrame = 0;
  const recommendationStepLabels = ['activity', 'foot characteristics', 'style'];
  const recommendationOptionGroups = {
    activity: { inputName: 'activity', answerKey: 'activity' },
    footCharacteristic: { inputName: 'foot_characteristic', answerKey: 'foot_characteristic' },
    designStyle: { inputName: 'design_style', answerKey: 'design_style' },
  };
  const recommendationOptionFallbackImages = {};
  root.querySelectorAll('[data-recommendation-options] label').forEach((label) => {
    const group = label.closest('[data-recommendation-options]')?.dataset.recommendationOptions;
    const value = label.querySelector('input')?.value;
    const image = label.querySelector('.fizo-recommendation-option-image');
    if (!group || !value || !image?.src) return;
    recommendationOptionFallbackImages[group] ||= {};
    recommendationOptionFallbackImages[group][value] = image.src;
  });

  const optionLabel = (value) => value
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

  const normalizedRecommendationConfig = (payload) => {
    const source = payload?.options;
    if (!source || typeof source !== 'object') return null;
    const options = {};
    for (const key of Object.keys(recommendationOptionGroups)) {
      if (!Array.isArray(source[key])) return null;
      options[key] = [...new Set(source[key]
        .filter((value) => typeof value === 'string')
        .map((value) => value.trim().toLowerCase())
        .filter((value) => /^[a-z0-9_]{1,64}$/.test(value)))];
      if (!options[key].length) return null;
    }
    const optionImages = {};
    const imageSource = payload?.optionImages;
    if (imageSource && typeof imageSource === 'object') {
      for (const [key, values] of Object.entries(imageSource)) {
        if (!options[key] || !values || typeof values !== 'object' || Array.isArray(values)) continue;
        optionImages[key] = {};
        for (const [rawValue, rawUrl] of Object.entries(values)) {
          const value = String(rawValue).trim().toLowerCase();
          if (!options[key].includes(value) || typeof rawUrl !== 'string' || rawUrl.length > 2048) continue;
          try {
            const url = new URL(rawUrl, window.location.origin);
            if (url.protocol === 'https:' || url.origin === window.location.origin) {
              optionImages[key][value] = url.href;
            }
          } catch (_) {
            // Ignore an invalid image URL while keeping the option selectable.
          }
        }
      }
    }
    return { options, optionImages };
  };

  const applyRecommendationConfig = (config) => {
    for (const [key, group] of Object.entries(recommendationOptionGroups)) {
      const container = form?.querySelector(`[data-recommendation-options="${key}"]`);
      if (!container) continue;
      const selectedValue = recommendationAnswers[group.answerKey];
      const fragment = document.createDocumentFragment();
      config.options[key].forEach((value, index) => {
        const label = document.createElement('label');
        const input = document.createElement('input');
        const text = document.createElement('span');
        const imageUrl = config.optionImages?.[key]?.[value]
          || recommendationOptionFallbackImages[key]?.[value];
        input.type = 'radio';
        input.name = group.inputName;
        input.value = value;
        input.required = index === 0;
        input.checked = selectedValue === value;
        text.textContent = optionLabel(value);
        label.append(input, text);
        if (imageUrl) {
          const image = document.createElement('img');
          image.className = 'fizo-recommendation-option-image';
          image.src = imageUrl;
          image.alt = '';
          image.width = 40;
          image.height = 32;
          image.decoding = 'async';
          label.append(image);
        }
        fragment.append(label);
      });
      container.replaceChildren(fragment);
    }

    let storedAnswers = {};
    try { storedAnswers = JSON.parse(window.sessionStorage.getItem(storageKey) || '{}') || {}; }
    catch (_) { storedAnswers = {}; }
    let answersChanged = false;
    for (const [key, group] of Object.entries(recommendationOptionGroups)) {
      const storedValue = storedAnswers[group.answerKey];
      if (storedValue && !config.options[key].includes(String(storedValue).toLowerCase())) {
        delete storedAnswers[group.answerKey];
        delete recommendationAnswers[group.answerKey];
        answersChanged = true;
      }
    }
    if (answersChanged) window.sessionStorage.setItem(storageKey, JSON.stringify(storedAnswers));
  };

  const loadRecommendationConfig = async () => {
    try {
      const cached = JSON.parse(window.sessionStorage.getItem(configStorageKey) || 'null');
      const cachedConfig = Number(cached?.expiresAt) > Date.now()
        ? normalizedRecommendationConfig(cached)
        : null;
      if (cachedConfig) {
        applyRecommendationConfig(cachedConfig);
        return;
      }
    } catch (_) {
      // Ignore an invalid browser cache and refresh it from the service.
    }
    if (!recommendationConfigEndpoint) return;
    try {
      const response = await fetch(recommendationConfigEndpoint, {
        headers: { Accept: 'application/json' },
        credentials: 'include',
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error('Recommendation configuration request failed.');
      const config = normalizedRecommendationConfig(payload);
      if (!config) throw new Error('Recommendation configuration is invalid.');
      applyRecommendationConfig(config);
      window.sessionStorage.setItem(configStorageKey, JSON.stringify({
        ...config,
        expiresAt: Date.now() + configCacheLifetime,
      }));
      if (dialog?.open) setRecommendationStep(recommendationStep, { scroll: false });
    } catch (_) {
      // Keep the server-rendered default options when configuration is unavailable.
    }
  };

  const syncRecommendationAlignment = () => {
    if (!dialog || !recommendationScroller) return;
    const target = recommendationSteps.find((item) => Number(item.dataset.recommendationStep) === recommendationStep);
    const isAligned = !target || Math.abs(recommendationScroller.scrollTop - target.offsetTop) <= 2;
    dialog.classList.toggle('is-between-recommendation-steps', !isAligned);
  };

  const firstMissingStep = (throughStep = 3) => recommendationSteps.slice(0, throughStep).findIndex((item) => !item.querySelector('input:checked')) + 1;
  const showMissingStep = (step) => {
    if (!step) return false;
    setRecommendationStep(step);
    if (status) status.textContent = `Please select an option for step ${step}: ${recommendationStepLabels[step - 1]}.`;
    return true;
  };

  const getAnonymousId = () => {
    const now = Date.now();
    try {
      const stored = JSON.parse(window.localStorage.getItem(anonymousKey) || 'null');
      if (stored?.value && Number(stored.expiresAt) > now) return stored.value;
    } catch (_) {
      // Legacy plain-string values are replaced with the expiring format below.
    }

    const value = window.crypto?.randomUUID?.() || `anon-${now}-${Math.random().toString(16).slice(2)}`;
    window.localStorage.setItem(anonymousKey, JSON.stringify({ value, expiresAt: now + anonymousLifetime }));
    return value;
  };

  const lockHomepageScroll = () => {
    if (dialogScrollState) return;
    const body = document.body;
    const html = document.documentElement;
    dialogScrollState = {
      scrollY: window.scrollY,
      body: {
        position: body.style.position,
        top: body.style.top,
        left: body.style.left,
        right: body.style.right,
        width: body.style.width,
        overflow: body.style.overflow
      },
      htmlOverflow: html.style.overflow
    };
    html.style.overflow = 'hidden';
    body.style.position = 'fixed';
    body.style.top = `-${dialogScrollState.scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    body.style.overflow = 'hidden';
  };

  const unlockHomepageScroll = () => {
    if (!dialogScrollState) return;
    const state = dialogScrollState;
    dialogScrollState = null;
    const body = document.body;
    const html = document.documentElement;
    body.style.position = state.body.position;
    body.style.top = state.body.top;
    body.style.left = state.body.left;
    body.style.right = state.body.right;
    body.style.width = state.body.width;
    body.style.overflow = state.body.overflow;
    html.style.overflow = state.htmlOverflow;
    window.scrollTo(0, state.scrollY);
  };

  const openDialog = () => {
    if (dialog?.showModal) dialog.showModal();
    else dialog?.setAttribute('open', '');
    lockHomepageScroll();
    try {
      recommendationAnswers = JSON.parse(window.sessionStorage.getItem(storageKey) || '{}') || {};
    } catch (_) {
      recommendationAnswers = {};
    }
    setRecommendationStep(1, { scroll: false });
    if (recommendationScroller) {
      recommendationScroller.scrollTop = 0;
      syncRecommendationAlignment();
    }
  };
  const closeDialog = () => {
    if (dialog?.close && dialog.open) dialog.close();
    else dialog?.removeAttribute('open');
    unlockHomepageScroll();
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-open-recommendations]');
    if (!trigger) return;
    event.preventDefault();
    openDialog();
  });
  if (window.location.hash === '#fizo-recommendation-dialog') {
    window.requestAnimationFrame(openDialog);
  }
  root.querySelectorAll('[data-close-recommendations]').forEach((button) => button.addEventListener('click', closeDialog));
  dialog?.addEventListener('click', (event) => {
    if (event.target !== dialog) return;
    const panel = dialog.getBoundingClientRect();
    const clickedInsidePanel = event.clientX >= panel.left && event.clientX <= panel.right && event.clientY >= panel.top && event.clientY <= panel.bottom;
    if (!clickedInsidePanel) closeDialog();
  });
  dialog?.addEventListener('cancel', (event) => event.preventDefault());
  dialog?.addEventListener('close', unlockHomepageScroll);

  const setRecommendationStep = (step, { scroll = true, behavior = 'smooth' } = {}) => {
    recommendationStep = Math.max(1, Math.min(4, step));
    recommendationSteps.forEach((item) => item.classList.toggle('is-current', Number(item.dataset.recommendationStep) === recommendationStep));
    form?.querySelectorAll('input[type="radio"]').forEach((input) => {
      input.checked = recommendationAnswers[input.name] === input.value;
    });
    if (stepNumber) stepNumber.textContent = recommendationStep;
    if (answerChip) {
      const previousStep = recommendationSteps.find((item) => Number(item.dataset.recommendationStep) === recommendationStep - 1);
      const previousValue = previousStep?.querySelector('input:checked')?.closest('label')?.querySelector('span')?.textContent?.trim();
      answerChip.textContent = previousValue || '';
      answerChip.hidden = recommendationStep === 1 || !previousValue;
    }
    progress?.classList.toggle('is-complete', recommendationStep === 4);
    progressSteps.forEach((item) => {
      const itemStep = Number(item.dataset.recommendationProgressStep);
      item.classList.toggle('is-active', itemStep === recommendationStep);
      item.classList.toggle('is-past', itemStep < recommendationStep);
      item.setAttribute('aria-current', itemStep === recommendationStep ? 'step' : 'false');
    });
    if (backButton) backButton.hidden = true;
    if (nextButton) nextButton.hidden = true;
    if (submitButton) submitButton.hidden = recommendationStep !== 4;
    if (status) status.textContent = '';
    if (scroll && recommendationScroller) {
      const target = recommendationSteps.find((item) => Number(item.dataset.recommendationStep) === recommendationStep);
      if (target) {
        dialog?.classList.add('is-between-recommendation-steps');
        recommendationScroller.scrollTo({ top: target.offsetTop, behavior });
        window.requestAnimationFrame(syncRecommendationAlignment);
      }
    } else {
      syncRecommendationAlignment();
    }
  };
  loadRecommendationConfig();
  const syncRecommendationStepFromScroll = () => {
    recommendationScrollFrame = 0;
    if (!recommendationScroller) return;
    const viewportCenter = recommendationScroller.getBoundingClientRect().top + (recommendationScroller.clientHeight / 2);
    const closest = recommendationSteps.reduce((best, item) => {
      const bounds = item.getBoundingClientRect();
      const distance = Math.abs(bounds.top + (bounds.height / 2) - viewportCenter);
      return !best || distance < best.distance ? { item, distance } : best;
    }, null)?.item;
    const visibleStep = Number(closest?.dataset.recommendationStep);
    if (visibleStep && visibleStep !== recommendationStep) setRecommendationStep(visibleStep, { scroll: false });
    syncRecommendationAlignment();
  };
  recommendationScroller?.addEventListener('scroll', () => {
    if (recommendationScrollFrame) return;
    recommendationScrollFrame = window.requestAnimationFrame(syncRecommendationStepFromScroll);
  }, { passive: true });
  nextButton?.addEventListener('click', () => {
    const current = recommendationSteps.find((item) => Number(item.dataset.recommendationStep) === recommendationStep);
    const selected = current?.querySelector('input:checked');
    if (recommendationStep < 4 && !selected) {
      current?.querySelector('input[required]')?.reportValidity();
      if (status) status.textContent = 'Please choose an option to continue.';
      return;
    }
    setRecommendationStep(recommendationStep + 1);
  });
  backButton?.addEventListener('click', () => setRecommendationStep(recommendationStep - 1));
  progressSteps.forEach((item) => item.addEventListener('click', () => {
    const requestedStep = Number(item.dataset.recommendationProgressStep);
    if (requestedStep > 1 && showMissingStep(firstMissingStep(Math.min(3, requestedStep - 1)))) return;
    setRecommendationStep(requestedStep);
  }));
  form?.addEventListener('click', (event) => {
    const label = event.target.closest?.('label');
    const input = label?.querySelector('input[type="radio"]');
    const inputStep = Number(label?.closest('[data-recommendation-step]')?.dataset.recommendationStep);
    if (!input || !inputStep || inputStep >= 4) return;
    if (inputStep !== recommendationStep) setRecommendationStep(inputStep, { scroll: false });
    const now = Date.now();
    if (input === lastRecommendationInput && now - lastRecommendationClickAt < 400) return;
    lastRecommendationInput = input;
    lastRecommendationClickAt = now;
    recommendationAnswers[input.name] = input.value;
    // A click handles both a new selection and a repeat click on an already checked radio.
    window.clearTimeout(pendingRecommendationAdvance);
    pendingRecommendationAdvance = window.setTimeout(() => {
      if (recommendationStep < 4) setRecommendationStep(recommendationStep + 1);
    }, 120);
  });

  const productTrack = root.querySelector('[data-product-grid]');
  root.querySelector('[data-home-products-prev]')?.addEventListener('click', () => productTrack?.scrollBy({ left: -(productTrack.clientWidth * 0.8), behavior: 'smooth' }));
  root.querySelector('[data-home-products-next]')?.addEventListener('click', () => productTrack?.scrollBy({ left: productTrack.clientWidth * 0.8, behavior: 'smooth' }));

  const categoryTrack = root.querySelector('[data-home-category-grid]');
  root.querySelector('[data-home-categories-prev]')?.addEventListener('click', () => categoryTrack?.scrollBy({ left: -(categoryTrack.clientWidth * 0.8), behavior: 'smooth' }));
  root.querySelector('[data-home-categories-next]')?.addEventListener('click', () => categoryTrack?.scrollBy({ left: categoryTrack.clientWidth * 0.8, behavior: 'smooth' }));

  const communityTrack = root.querySelector('[data-home-community-grid]');
  const communityCarousel = root.querySelector('[data-home-community-carousel]');
  const syncCommunityCarousel = () => {
    if (!communityTrack || !communityCarousel) return;
    const max = Math.max(communityTrack.scrollWidth - communityTrack.clientWidth, 0);
    communityCarousel.querySelector('[data-home-community-prev]')?.toggleAttribute('disabled', communityTrack.scrollLeft <= 2);
    communityCarousel.querySelector('[data-home-community-next]')?.toggleAttribute('disabled', communityTrack.scrollLeft >= max - 2);
  };
  communityCarousel?.querySelector('[data-home-community-prev]')?.addEventListener('click', () => communityTrack?.scrollBy({ left: -communityTrack.clientWidth, behavior: 'smooth' }));
  communityCarousel?.querySelector('[data-home-community-next]')?.addEventListener('click', () => communityTrack?.scrollBy({ left: communityTrack.clientWidth, behavior: 'smooth' }));
  communityTrack?.addEventListener('scroll', syncCommunityCarousel, { passive: true });
  if (communityTrack && window.ResizeObserver) new ResizeObserver(syncCommunityCarousel).observe(communityTrack);
  syncCommunityCarousel();

  const requestRecommendations = async (answers) => {
    if (!recommendationEndpoint) throw new Error('Recommendation endpoint is not configured.');
    const payload = {
      action: 'recommend',
      context: recommendationContext,
      answers: {
        activity: answers.activity,
        footCharacteristic: answers.footCharacteristic || answers.foot_characteristic,
        designStyle: answers.designStyle || answers.design_style,
      },
      ...(authenticated ? {} : { anonymousId: getAnonymousId() }),
    };
    const response = await fetch(recommendationEndpoint, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, credentials: 'include', body: JSON.stringify(payload) });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.message || data.error || `Recommendation request failed (${response.status}).`);
    const products = window.FizoRecommendationResponse.products(data);
    return { ...data, products };
  };

  form?.addEventListener('submit', async (event) => {
    event.preventDefault();
    const answers = { ...recommendationAnswers, ...Object.fromEntries(new FormData(form).entries()) };
    if (showMissingStep(firstMissingStep(3))) return;
    window.sessionStorage.setItem(storageKey, JSON.stringify(answers));
    status.textContent = 'Finding your best match…';
    form.querySelector('button[type="submit"]').disabled = true;
    try {
      const result = await requestRecommendations(answers);
      window.sessionStorage.setItem(resultStorageKey, JSON.stringify({
        products: result.products,
        recommendationId: result.recommendationId || result.id || '',
        answers,
        savedAt: Date.now()
      }));
      closeDialog();
      window.location.assign(recommendationResultsUrl);
    } catch (error) { status.textContent = error.message; } finally { form.querySelector('button[type="submit"]').disabled = false; }
  });

  const slides = [...root.querySelectorAll('[data-home-hero-slide]')];
  const currentSlide = root.querySelector('[data-home-hero-current]');
  const totalSlide = root.querySelector('[data-home-hero-total]');
  const progressBar = root.querySelector('[data-home-hero-progress]');
  const usesStaticAnonymousHero = () => !authenticated;
  let slideIndex = 0;
  let slideTimer;
  const showSlide = (nextIndex) => {
    if (!slides.length) return;
    slideIndex = (nextIndex + slides.length) % slides.length;
    const activeSlide = slides[slideIndex];
    slides.forEach((slide, index) => {
      slide.classList.toggle('is-active', index === slideIndex);
      slide.setAttribute('aria-hidden', index === slideIndex ? 'false' : 'true');
      const video = slide.querySelector('video');
      if (video) {
        if (index === slideIndex) video.play?.().catch(() => {});
        else video.pause();
      }
    });
    if (currentSlide) currentSlide.textContent = String(slideIndex + 1);
    if (totalSlide) totalSlide.textContent = String(slides.length);
    if (progressBar) {
      progressBar.style.width = `${100 / slides.length}%`;
      progressBar.style.left = `${(100 / slides.length) * slideIndex}%`;
      // The progress indicator uses the first slide's theme color for every slide.
      progressBar.style.backgroundColor = '#ecff80';
    }
  };
  const restartSlideTimer = () => {
    window.clearInterval(slideTimer);
    if (usesStaticAnonymousHero() || slides.length <= 1) return;
    slideTimer = window.setInterval(() => showSlide(slideIndex + 1), 6000);
  };
  root.querySelector('[data-home-hero-prev]')?.addEventListener('click', () => {
    if (usesStaticAnonymousHero()) return;
    showSlide(slideIndex - 1);
    restartSlideTimer();
  });
  root.querySelector('[data-home-hero-next]')?.addEventListener('click', () => {
    if (usesStaticAnonymousHero()) return;
    showSlide(slideIndex + 1);
    restartSlideTimer();
  });
  showSlide(0);
  restartSlideTimer();

  const brandScene = root.querySelector('.fizo-home-brand-promotion');
  const brandSteps = [...(brandScene?.querySelectorAll('[data-home-brand-step]') || [])];
  let brandFrame = 0;
  const updateBrandSteps = () => {
    brandFrame = 0;
    if (!brandScene || !brandSteps.length) return;
    const sceneBounds = brandScene.getBoundingClientRect();
    const media = brandScene.querySelector('.fizo-home-brand-promotion__media');
    const mediaHeight = media ? media.getBoundingClientRect().height : window.innerHeight;
    // The scene is only the sticky media viewport plus the three transitions
    // for steps 2-4. Do not use the browser viewport here: the desktop canvas
    // is scaled from 1920px and its media can be a few pixels shorter/taller.
    const unlockDistance = Math.max(0, sceneBounds.height - mediaHeight);
    const stageDuration = Math.max(1, unlockDistance / brandSteps.length);
    const animationActive = sceneBounds.top <= 0 && sceneBounds.bottom >= mediaHeight;
    brandSteps.forEach((layer) => {
      const stepNumber = Number(layer.dataset.homeBrandStep) - 2;
      const progress = Math.max(0, Math.min(1, (-sceneBounds.top - stepNumber * stageDuration) / stageDuration));
      const card = layer.querySelector('.fizo-home-step');
      if (!card) return;
      const offset = (1 - progress) * 35;
      const settledProgress = sceneBounds.top > 0 ? 0 : 1;
      card.style.opacity = (animationActive ? progress : settledProgress).toFixed(3);
      card.style.transform = `translateY(${animationActive ? offset : (settledProgress ? 0 : 35)}rem)`;
    });
  };
  if (brandScene && brandSteps.length) {
    const scheduleBrandSteps = () => { if (!brandFrame) brandFrame = window.requestAnimationFrame(updateBrandSteps); };
    window.addEventListener('scroll', scheduleBrandSteps, {passive: true});
    window.addEventListener('resize', scheduleBrandSteps, {passive: true});
    scheduleBrandSteps();
  }
})();
